<template>
  <section class="projects-3d-list-block">
    <div class="container">
      <template v-if="heading">
        <div class="projects-3d-list-block__head">
          <h2 class="projects-3d-list-block__title">{{ heading }}</h2>
          <nuxt-link v-if="isDesktop" to="/3d-projects" class="projects-3d-list-block__link">
            Посмотреть все 3D-проекты
            <img
              src="~/assets/img/svg/common/arrow.svg"
              alt="стрелка"
              width="14"
              height="14"
              loading="lazy"
              decoding="async"
            />
          </nuxt-link>
        </div>
      </template>
      <template v-if="description">
        <div class="projects-3d-list-block__description">{{ description }}</div>
      </template>

      <template v-if="isDesktop">
        <div
          class="projects-3d-list-block__swiper"
          :style="swiperStyle"
        >
          <div
            v-for="(slide, index) in projects"
            :key="`slide-${slide.id || index}`"
            :class="{'--active': index === activeSlide}"
          >
            <Card
              :item="slide"
              :active="index === activeSlide"
              @click="setSlide(index)"
              @prevSlide="prevSlide"
              @nextSlide="nextSlide"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <MobileTemplate key="project-3d-list" :slides="projects as any"/>
      </template>

      <NuxtLink
        v-if="!isDesktop"
        to="/3d-projects"
        class="btn btn-outline projects-3d-list-block__btn-more"
      >
        Посмотреть все 3D-проекты
      </NuxtLink>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { IBlockDefaultProps } from '#sitis/internal/models/common';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { ArticleItem } from '#sitis/internal/controllers/articles/models/ArticleItem';

const Card = defineAsyncComponent(() => import('~/components/accordion-gallery/Card.vue'));
const MobileTemplate = defineAsyncComponent(() => import('~/components/accordion-gallery/MobileTemplate.vue'));

const props = defineProps<IBlockDefaultProps>();
const { $api, $viewport } = useNuxtApp();

// Computed properties
const isDesktop: ComputedRef<boolean> = computed(() => $viewport.isGreaterOrEquals('lg'));

const heading: ComputedRef<string> = computed(() => props.values?.heading || '');

const description: ComputedRef<string> = computed(() => {
  return isDesktop.value
    ? (props.values?.description_desktop || '')
    : (props.values?.description_mobile || '');
});

// Slider state
const activeSlide: Ref<number> = ref(0);

const swiperStyle = computed(() => ({
  '--active-index': activeSlide.value
}));

const prevSlide = (): void => {
  const newVal = activeSlide.value - 1;
  activeSlide.value = newVal < 0 ? projects.value.length - 1 : newVal;
};

const nextSlide = (): void => {
  const newVal = activeSlide.value + 1;
  activeSlide.value = newVal > projects.value.length - 1 ? 0 : newVal;
};

const setSlide = (index: number): void => {
  activeSlide.value = index;
};

// Projects data
const projects: Ref<ArticleItem[]> = ref([]);

const getProjects = async (): Promise<void> => {
  try {
    const searchParams = [
      'page=1',
      props.cfgs?.limit && `per-page=${props.cfgs.limit}`,
      props.cfgs?.sort && `sort=${props.cfgs.sort_direction === 3 ? '-' : ''}${props.cfgs.sort}`
    ].filter(Boolean).join('&');

    const res = await $api.agent.get(`/projects?${searchParams}`);

    if (res?._data) {
      projects.value = res._data.map((item: any) => new ArticleItem(item));
    }
  } catch (error) {
    console.error('Failed to fetch 3D projects:', error);
    projects.value = [];
  }
};

// Fetch data with caching and deduplication
useLazyAsyncData('get-articles-list-3d-cms', () => getProjects(), {
  server: true,
  dedupe: 'defer'
});
</script>

<style lang="scss" scoped>
.projects-3d-list-block {
  --pdlb-width-card: 196px;
  --pdlb-width-card-open: 847px;
  --pdlb-spacing-card: 32px;

  padding: 22px 0;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
}

.projects-3d-list-block__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
}

.projects-3d-list-block__link {
  display: flex;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  align-items: center;
  color: var(--black);
  gap: 6px;
  text-decoration: none;
  transition: opacity 0.2s ease;
  will-change: opacity;

  &:hover {
    opacity: 0.7;
  }

  img {
    transform: rotate(180deg);
    width: 14px;
    height: 14px;
  }
}

.projects-3d-list-block__description {
  font-weight: 500;
  line-height: 130%;
  padding-bottom: 32px;
}

.projects-3d-list-block__swiper {
  display: flex;
  transform: translateX(calc((var(--pdlb-width-card) * var(--active-index) + var(--pdlb-spacing-card) * var(--active-index)) * -1));
  transition: transform 0.3s ease;
  will-change: transform;

  > * {
    width: var(--pdlb-width-card);
    min-width: var(--pdlb-width-card);
    margin-left: var(--pdlb-spacing-card);
    transition: width 0.3s ease, min-width 0.3s ease;
    will-change: width, min-width;

    &.--active {
      width: var(--pdlb-width-card-open);
      min-width: var(--pdlb-width-card-open);
    }

    &:first-child {
      margin-left: 0;
    }
  }
}

.projects-3d-list-block__btn-more {
  display: none;
}

@media (max-width: 1699px) {
  .projects-3d-list-block {
    --pdlb-spacing-card: 20px;
  }
}

@media (max-width: 640px) {
  .projects-3d-list-block__btn-more {
    padding: 15px 24px;
    margin-top: 32px;
    display: flex;
    border-color: var(--black);
    font-weight: 600;
  }
}
</style>
